import type { ReactElement } from "react";
import { documentsFixture } from "../api/fixtures";
import { Badge, Stack, Surface, Text, ValueUnavailable } from "../ui-kit";
import { journeyUsersFixture } from "./fixtures";
import type { JourneyStage, JourneyStageCount } from "./journey";
import { computeJourneyStages } from "./journey";

function StageCount({
  count,
  unavailableReason,
}: {
  count: JourneyStageCount;
  unavailableReason?: string | undefined;
}): ReactElement {
  if (count === null) {
    return <ValueUnavailable reason={unavailableReason ?? "No event captured for this channel."} />;
  }
  return (
    <Text as="span" variant="bodyLarge">
      {count}
    </Text>
  );
}

function StageCard({ stage }: { stage: JourneyStage }): ReactElement {
  return (
    <Surface as="li" padding="default">
      <Stack gap={3}>
        {/* h3 — directly under the section's own h2 ("User journey"), no
            intermediate level between them. */}
        <Text as="h3" variant="subheading">
          {stage.label}
        </Text>
        <Stack direction="row" gap={4}>
          <Stack gap={1}>
            <Badge tone="info">Mobile</Badge>
            <StageCount count={stage.mobile} unavailableReason={stage.unavailableReason} />
          </Stack>
          <Stack gap={1}>
            <Badge tone="neutral">Telegram</Badge>
            <StageCount count={stage.telegram} unavailableReason={stage.unavailableReason} />
          </Stack>
        </Stack>
      </Stack>
    </Surface>
  );
}

/**
 * Registration → session → document-upload journey funnel (ISSUE-352 AC1).
 * Fixture-backed: `journeyUsersFixture` (`./fixtures.ts`) for the per-user
 * side and `documentsFixture` (`src/api/fixtures.ts`, the same `GET
 * /documents` shape `DocumentsView` consumes) for the document side.
 * `GET /users` returns a bare count as of ISSUE-455 Deliverable 3, so this
 * funnel stays fixture-only — it can never be wired to a live per-user
 * roster from that endpoint.
 */
export function JourneyFunnel(): ReactElement {
  const stages = computeJourneyStages(journeyUsersFixture, documentsFixture.documents);
  return (
    <Surface as="section" aria-label="User journey">
      <Stack gap={4}>
        {/* h2 — the dashboard screen's first section heading, directly under
            the view's own h1 (DashboardView assembly). */}
        <Stack direction="row" gap={2} align="center" wrap>
          <Text variant="heading" as="h2">
            User journey
          </Text>
          <Badge tone="info">Sample data</Badge>
        </Stack>
        <Stack as="ol" direction="row" gap={4} wrap>
          {stages.map((stage) => (
            <StageCard key={stage.key} stage={stage} />
          ))}
        </Stack>
      </Stack>
    </Surface>
  );
}
